import { ApiError, api } from '../api/client';
import { track } from '../telemetry';
import { bestPlankPermutation, cloneState, detectPatternsForPlayer, resolveTurn } from '../engine/resolver';
import { getCardRegistry } from '../engine/shop';
import { antesFanfare, captureCardRects, coinTrail, floatingDelta, playFlip, flashComboBanner, setHullPulse, showLoseCeremony, showWinCeremony, sparkleOnElement, tweenNumber, screenShake, flashOverlay, damageNumber, lowHullVignette, } from './animations';
import { play as playSfx } from '../audio/registry';
import { mixer } from '../audio/mixer';
import { shipArt } from './art';
import { fireCannons } from './animations';
void mixer; // referenced for type & unlock; consumed by lobby's settings UI
function patternSfx(type) {
    switch (type) {
        case 'double': return 'pattern_double';
        case 'triple': return 'pattern_triple';
        case 'sandwich': return 'pattern_sandwich';
        case 'marriage': return 'pattern_marriage';
        case 'run': return 'pattern_run';
        case 'suit-chain': return 'pattern_suit_chain';
        case 'flush': return 'pattern_flush';
        case 'straight-flush': return 'pattern_straight_flush';
        case 'royalty': return 'pattern_royalty';
        case 'mutiny': return 'pattern_mutiny';
        case 'parity-squad': return 'pattern_parity';
        default: return 'card_play';
    }
}
import { attachDragHandlers, attachLongPressPreview, showCardPreview } from './drag-drop';
import { renderCrewPortrait } from './crew-art';
void renderCrewPortrait;
import { renderCrewCard } from './crew-card';
import { renderPlayingCard } from './card';
// Captain + ship display-name lookups, populated on first match-view mount.
// Falls back to id strings if progression hasn't loaded yet.
const _progNames = {
    captains: new Map(),
    ships: new Map(),
};
let _progLoaded = false;
async function _ensureProgNames() {
    if (_progLoaded)
        return;
    try {
        const prog = await api.progression();
        for (const c of prog.captains)
            _progNames.captains.set(c.id, c.name);
        for (const s of prog.ships)
            _progNames.ships.set(s.id, s.name);
        _progLoaded = true;
    }
    catch {
        // Best-effort — names just stay as ids if the API hiccups.
    }
}
function captainLabel(id) {
    if (!id)
        return '';
    return _progNames.captains.get(id) ?? `Captain ${id}`;
}
function shipLabel(id) {
    if (!id)
        return '';
    return _progNames.ships.get(id) ?? id;
}
let view = null;
// Refresh on tab focus / visibility — module-level so we add listeners once.
let visibilityHandler = null;
let focusHandler = null;
export function mountMatchView(root, matchId, goToLobby) {
    if (view?.pollHandle)
        clearTimeout(view.pollHandle);
    view = {
        matchId, envelope: null, selectedCardIds: [], lastError: '', pollHandle: null,
        shownDoubloons: { a: 0, b: 0 },
        lastPollAt: 0, pollCount: 0, lastEnvelopeHash: '',
    };
    // Fresh match → reset stage-sync dedupe so first plank change pushes.
    _plankSyncLastSent = '';
    if (_plankSyncTimer !== null) {
        clearTimeout(_plankSyncTimer);
        _plankSyncTimer = null;
    }
    // Detach any prior listeners (route change re-mount)
    if (visibilityHandler)
        document.removeEventListener('visibilitychange', visibilityHandler);
    if (focusHandler)
        window.removeEventListener('focus', focusHandler);
    visibilityHandler = () => {
        if (!document.hidden && view?.matchId === matchId)
            refresh(root, goToLobby);
    };
    focusHandler = () => {
        if (view?.matchId === matchId)
            refresh(root, goToLobby);
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    window.addEventListener('focus', focusHandler);
    track('match_started', { match_id: matchId });
    _ensureProgNames(); // fire and forget; first render may show ids, second sees names
    refresh(root, goToLobby, true);
}
export function unmountMatchView() {
    if (view?.pollHandle) {
        clearTimeout(view.pollHandle);
        view.pollHandle = null;
    }
    if (visibilityHandler)
        document.removeEventListener('visibilitychange', visibilityHandler);
    if (focusHandler)
        window.removeEventListener('focus', focusHandler);
    visibilityHandler = null;
    focusHandler = null;
    view = null;
}
function hashEnvelope(env) {
    if (!env)
        return '';
    const m = env.match;
    // Capture only fields that should trigger a re-render. Excludes things
    // like RNG state. Doubloon changes count via player state; new log lines
    // and turn cycling are captured by turnNumber + log length + status.
    return JSON.stringify({
        s: m.status,
        rs: env.row_status,
        t: m.turnOf,
        n: m.turnNumber,
        ll: m.log.length,
        a: { d: m.players.a.doubloons, hand: m.players.a.hand.map((c) => c.id), crew: m.players.a.crew.map((c) => c.id), pd: m.players.a.pendingDiscards, pm: m.players.a.pendingMarks ?? 0 },
        b: { d: m.players.b.doubloons, hand: m.players.b.hand.map((c) => c.id), crew: m.players.b.crew.map((c) => c.id), pd: m.players.b.pendingDiscards, pm: m.players.b.pendingMarks ?? 0 },
        sh: m.shop ? { o: m.shop.offers.map((o) => o.id), p: m.shop.currentPick } : null,
    });
}
async function refresh(root, goToLobby, initial = false) {
    if (!view)
        return;
    let envChanged = false;
    let prevAnte = view.envelope?.match?.currentAnte;
    try {
        const env = await api.getMatch(view.matchId);
        const isFirst = view.envelope === null;
        const newHash = hashEnvelope(env);
        envChanged = newHash !== view.lastEnvelopeHash;
        view.envelope = env;
        view.lastEnvelopeHash = newHash;
        view.lastError = '';
        if (isFirst) {
            view.shownDoubloons = {
                a: env.match.players.a.doubloons,
                b: env.match.players.b.doubloons,
            };
            prevAnte = env.match.currentAnte;
        }
        // PvE: ante just advanced → fanfare
        const newAnte = env.match.currentAnte;
        if (env.match.mode === 'pve' && prevAnte !== undefined && newAnte !== undefined
            && newAnte > prevAnte && env.match.status === 'in_progress') {
            antesFanfare(`ANTE ${newAnte}`);
            playSfx('ante_clear');
        }
    }
    catch (err) {
        view.lastError = err instanceof ApiError ? err.message : err.message;
        if (typeof console !== 'undefined')
            console.warn('[plunder] poll error:', view.lastError);
        envChanged = true; // re-render to surface the error if it's the first one
    }
    view.lastPollAt = Date.now();
    view.pollCount += 1;
    // Skip the DOM rewrite when nothing changed — preserves scroll position
    // in the log and stops mid-read flicker.
    if (envChanged || initial) {
        try {
            renderPreservingScroll(root, goToLobby);
        }
        catch (err) {
            if (typeof console !== 'undefined')
                console.error('[plunder] render error:', err);
        }
    }
    else {
        // Just update the poll-meta line if the waiting screen is showing.
        const meta = root.querySelector('.poll-meta');
        if (meta && view.lastPollAt) {
            meta.textContent = `last update ${new Date(view.lastPollAt).toLocaleTimeString()} · ${view.pollCount} checks`;
        }
    }
    schedulePoll(root, goToLobby);
    if (initial)
        return;
}
function renderPreservingScroll(root, goToLobby) {
    // Newest-first log: new entries are PREPENDED. To keep the user reading
    // the same entry across re-renders, we have to push their scrollTop
    // forward by however much new content was added at the top.
    const oldLog = root.querySelector('.log');
    const oldScrollTop = oldLog?.scrollTop ?? 0;
    const oldScrollHeight = oldLog?.scrollHeight ?? 0;
    const wasAtTop = oldScrollTop < 4;
    render(root, goToLobby);
    const newLog = root.querySelector('.log');
    if (!newLog)
        return;
    if (wasAtTop) {
        // Snap to top so the freshly prepended entry is visible.
        newLog.scrollTop = 0;
    }
    else {
        // Compensate for prepended height — keeps the same entry under the cursor.
        const delta = newLog.scrollHeight - oldScrollHeight;
        newLog.scrollTop = oldScrollTop + delta;
    }
}
function schedulePoll(root, goToLobby) {
    if (!view)
        return;
    if (view.pollHandle)
        clearTimeout(view.pollHandle);
    const env = view.envelope;
    if (!env)
        return;
    if (env.row_status === 'finished')
        return;
    const m = env.match;
    const isMyTurn = m.turnOf === env.perspective;
    const shopOpen = m.shop !== null;
    const myPick = shopOpen && m.shop.pickOrder[m.shop.currentPick] === env.perspective;
    // Tight polling when waiting on opponent so it feels live; lighter cadence
    // when it's our turn just to catch out-of-band changes (e.g. a forfeit).
    // Browsers throttle background tabs; visibility/focus listeners cover that.
    const interval = (!isMyTurn && !myPick) ? 1500 : 8000;
    view.pollHandle = window.setTimeout(() => refresh(root, goToLobby), interval);
}
function render(root, goToLobby) {
    if (!view)
        return;
    const env = view.envelope;
    if (!env) {
        root.innerHTML = `<div class="error">${escape(view.lastError || 'Loading…')}</div><button id="back" class="btn btn--ghost">Back to lobby</button>`;
        root.querySelector('#back')?.addEventListener('click', goToLobby);
        return;
    }
    const m = env.match;
    const me = m.players[env.perspective];
    const them = m.players[env.perspective === 'a' ? 'b' : 'a'];
    if (env.row_status === 'awaiting_opponent') {
        root.innerHTML = renderWaitingForJoin(m, env.perspective);
        bindBack(root, goToLobby);
        return;
    }
    if (m.status === 'finished') {
        if (view && !view.completedTracked) {
            view.completedTracked = true;
            track('match_completed', {
                match_id: view.matchId,
                winner: m.winner ?? null,
                end_reason: m.endReason ?? null,
                won: m.winner ? m.winner === env.perspective : null,
                mode: m.mode ?? 'pvp',
            });
        }
        root.innerHTML = renderEndScreen(m, env.perspective);
        bindBack(root, goToLobby);
        return;
    }
    const isMyTurn = m.turnOf === env.perspective;
    const shopOpen = m.shop !== null;
    const myPick = shopOpen && m.shop.pickOrder[m.shop.currentPick] === env.perspective;
    if (shopOpen && myPick) {
        root.innerHTML = renderShop(m, me);
        bindShop(root, goToLobby);
        return;
    }
    if (shopOpen && !myPick) {
        root.innerHTML = renderWaiting(m, them, 'is shopping');
        bindBack(root, goToLobby);
        return;
    }
    if (!isMyTurn) {
        const isFirstTurn = m.turnNumber === 1 && env.perspective === 'b' && m.log.length <= 1;
        const subtitle = isFirstTurn
            ? "You're in the match. The host plays first — sit tight."
            : 'Polling every 5s. Or come back later — your match waits.';
        root.innerHTML = renderWaiting(m, them, 'is plotting their move', subtitle);
        bindBack(root, goToLobby);
        return;
    }
    root.innerHTML = renderTurn(m, me, them);
    bindTurn(root, goToLobby);
    tweenDoubloonsToCurrent(root, m);
    // PvP only: push the player's in-progress plank to the server (debounced)
    // so the waiting opponent sees what's queued. PvE has no audience, skip.
    if (m.mode !== 'pve')
        schedulePlankSync();
}
let _plankSyncTimer = null;
let _plankSyncLastSent = '';
function schedulePlankSync() {
    if (!view)
        return;
    if (_plankSyncTimer !== null)
        clearTimeout(_plankSyncTimer);
    _plankSyncTimer = window.setTimeout(() => {
        _plankSyncTimer = null;
        if (!view)
            return;
        const sig = view.selectedCardIds.join(',');
        if (sig === _plankSyncLastSent)
            return;
        _plankSyncLastSent = sig;
        void api.stagePlank(view.matchId, [...view.selectedCardIds]).catch(() => {
            // Don't disrupt the player if staging fails — submit still works.
            _plankSyncLastSent = ''; // retry next change
        });
    }, 250);
}
function tweenDoubloonsToCurrent(root, m) {
    if (!view)
        return;
    const targets = [
        ['a', root.querySelector(`.hull-num[data-side="${m.players.a.id === view.envelope.perspective ? 'me' : 'them'}"]`)],
        ['b', root.querySelector(`.hull-num[data-side="${m.players.b.id === view.envelope.perspective ? 'me' : 'them'}"]`)],
    ];
    for (const [pid, el] of targets) {
        if (!el)
            continue;
        const target = m.players[pid].doubloons;
        const from = view.shownDoubloons[pid];
        if (from === target) {
            el.textContent = String(target);
        }
        else {
            if (target > from) {
                sparkleOnElement(el, '#f0c75a');
            }
            else if (target < from) {
                sparkleOnElement(el, '#a83232');
            }
            tweenNumber(from, target, 1600, (v) => { el.textContent = String(v); });
            view.shownDoubloons[pid] = target;
        }
    }
    // Hull bar pulse: danger when low, glory when near win threshold
    const me = m.players[view.envelope.perspective];
    const myHull = root.querySelector('.captain--me .hull-bar');
    if (myHull) {
        if (me.doubloons <= 8)
            setHullPulse(myHull, 'danger');
        else if (me.doubloons >= m.winThreshold - 8)
            setHullPulse(myHull, 'glory');
        else
            setHullPulse(myHull, null);
    }
}
function renderTurn(m, me, them) {
    // Defensive dedupe: a stray race in the drag system can sneak a duplicate
    // ID into selectedCardIds, which makes the plank render the same card
    // twice while the hand only highlights it once. Always read through a
    // unique-ID lens.
    const uniqueSelected = Array.from(new Set(view.selectedCardIds));
    if (uniqueSelected.length !== view.selectedCardIds.length) {
        view.selectedCardIds = uniqueSelected;
    }
    // Also drop any IDs that aren't in the current hand (e.g., a tossed card
    // that was previously selected).
    const handIds = new Set(me.hand.map((c) => c.id));
    const cleaned = uniqueSelected.filter((id) => handIds.has(id));
    if (cleaned.length !== uniqueSelected.length) {
        view.selectedCardIds = cleaned;
    }
    let plank = view.selectedCardIds
        .map((id) => me.hand.find((c) => c.id === id))
        .filter(Boolean);
    // Tactician (client-side mirror): the server reorders the plank for max
    // value before resolving, so reflect that in the UI immediately. Player
    // drops a card → plank visibly rearranges into its optimal order.
    if (plank.length >= 2 && me.crew.some((c) => c.id === 'tactician')) {
        const reordered = bestPlankPermutation(plank, me);
        if (reordered.some((c, i) => c.id !== plank[i].id)) {
            plank = reordered;
            view.selectedCardIds = reordered.map((c) => c.id);
        }
    }
    // Run the real resolver against a clone to get the FULL preview
    // (patterns + crew triggers + final earn/steal). This matches the
    // server-side result exactly, so what you see is what you get.
    let preview = null;
    if (plank.length > 0) {
        try {
            const cloned = cloneState(m);
            preview = resolveTurn(cloned, plank).result;
        }
        catch (err) {
            if (typeof console !== 'undefined')
                console.error('[plunder] preview error:', err);
        }
    }
    const handHtml = me.hand.map((c) => {
        const inPlank = view.selectedCardIds.includes(c.id);
        return renderPlayingCard(c, { selected: inPlank });
    }).join('');
    const plankHtml = plank.map((c) => renderPlayingCard(c)).join('') ||
        '<div class="plank-empty">Tap cards from your hand to board the Plank.</div>';
    const marksActive = me.pendingMarks ?? 0;
    const previewItems = [];
    if (preview) {
        // Dedupe identical crew-trigger lines (e.g., Echo doubling a pattern
        // produces two identical Bosun triggers). Group with ×N multiplier.
        const seen = new Map();
        for (const t of preview.crewTriggers) {
            if (t.crewId === 'mark')
                continue;
            const key = `${t.crewId}|${t.note}|${t.earn}|${t.steal}`;
            const prev = seen.get(key);
            if (prev)
                prev.count += 1;
            else
                seen.set(key, { note: t.note, earn: t.earn, steal: t.steal, count: 1 });
        }
        for (const v of seen.values()) {
            const mult = v.count > 1 ? ` <span class="preview-mult">×${v.count}</span>` : '';
            const totalEarn = v.earn * v.count;
            const totalSteal = v.steal * v.count;
            const swing = totalEarn !== 0
                ? ` (${totalEarn > 0 ? '+' : ''}${totalEarn})`
                : (totalSteal !== 0 ? ` (steal ${totalSteal})` : '');
            previewItems.push(`<li class="crew-trigger">⚓ ${escape(v.note)}${mult}${swing}</li>`);
        }
        // Per-pattern lines with crew-bonus tags. Dedupe by pattern label + bonus.
        const patSeen = new Map();
        for (const pb of preview.patternBreakdown) {
            const key = `${pb.pattern.label}|${pb.earn}|${pb.steal}|${pb.pattern.forceDiscard ?? 0}|${pb.note ?? ''}`;
            const prev = patSeen.get(key);
            if (prev)
                prev.count += 1;
            else
                patSeen.set(key, {
                    label: pb.pattern.label, earn: pb.earn, steal: pb.steal,
                    fd: pb.pattern.forceDiscard ?? 0, note: pb.note ?? '', count: 1,
                });
        }
        for (const v of patSeen.values()) {
            const tag = v.note ? `<span class="preview-tag">${escape(v.note.trim().replace(/^\(|\)$/g, ''))}</span>` : '';
            const mult = v.count > 1 ? ` <span class="preview-mult">×${v.count}</span>` : '';
            previewItems.push(`<li>${escape(v.label)}${mult}: ${v.earn ? `+${v.earn}` : ''}${v.steal ? ` steal ${v.steal}` : ''}${v.fd ? ` force discard ${v.fd}` : ''} ${tag}</li>`);
        }
    }
    const detailCount = previewItems.length;
    const patternsPreview = previewItems.join('') || '<li class="muted">No patterns yet.</li>';
    // Compact "stack" headline — patterns deduped with multipliers, shown
    // above the total. Lets the player read "Run · Suit Chain ×2" at a glance
    // instead of scrolling through every line.
    let stackLine = '';
    if (preview && preview.patterns.length > 0) {
        const counts = new Map();
        for (const p of preview.patterns)
            counts.set(p.label, (counts.get(p.label) ?? 0) + 1);
        const chips = [...counts.entries()].map(([label, n]) => `<span class="preview-chip">${escape(label)}${n > 1 ? ` ×${n}` : ''}</span>`).join('');
        stackLine = `<div class="preview-stack">${chips}</div>`;
    }
    // Total summary line — what you'll actually gain/lose this turn.
    let totalLine = '';
    if (preview && plank.length > 0) {
        const earn = preview.effects.earn;
        const steal = preview.effects.steal;
        const fd = preview.effects.forceDiscard;
        const parts = [];
        if (earn !== 0)
            parts.push(`<span class="${earn > 0 ? 'pos' : 'neg'}">${earn > 0 ? '+' : ''}${earn} earn</span>`);
        if (steal > 0)
            parts.push(`<span class="pos">steal ${steal}</span>`);
        if (fd > 0)
            parts.push(`<span class="neg">opp discards ${fd}</span>`);
        totalLine = parts.length > 0
            ? `<div class="preview-total">If you resolve: ${parts.join(' · ')}</div>`
            : '<div class="preview-total muted">No swing this turn.</div>';
    }
    const markNotice = marksActive > 0
        ? `<div class="mark-notice">⚓ X Marks the Spot: next ${marksActive === 1 ? 'pattern is' : `${marksActive} patterns are`} doubled.</div>`
        : '';
    return `
    <div class="match-view match-view--v2">
      <!-- Top bar: log on left, mode badge center, back-to-lobby on right.
           Hand counter lives in the ante panel below; turn counter is shown
           inline for PvP only. -->
      <div class="match-topbar">
        <button id="btn-log" class="match-icon-btn" type="button" title="View match log">📜</button>
        <div class="turn-indicator">
          ${m.mode === 'pve'
        ? `<span class="mode-badge mode-badge--pve">⚓ Solo Run</span>`
        : `<span class="mode-badge mode-badge--pvp">⚔ Turn ${m.turnNumber}</span>`}
        </div>
        <button id="btn-back" class="btn btn--ghost btn--small" type="button" title="Back to lobby">Back to lobby</button>
      </div>

      <!-- Ship section: opponent (or Treasury Galleon in solo) -->
      <section class="ship-zone ship-zone--them">
        ${m.mode === 'pve' ? renderAntePanel(m) : `
          <div class="captain captain--them">
            <div class="captain-name"><span class="captain-ship-icon" data-ship-side="them">${shipArt(them.shipId ?? m.shipId ?? 'brigantine')}</span>${escape(them.name)}</div>
            <div class="captain-loadout">${escape(captainLabel(them.captainId))} · ${escape(shipLabel(them.shipId ?? 'brigantine'))}</div>
            <div class="hull">🪙 <span class="hull-num" data-side="them">${view.shownDoubloons[them.id]}</span> <span class="hull-bar"><span style="width:${hullWidth(them.doubloons, m.winThreshold)}%"></span></span></div>
            <div class="captain-meta">
              <button class="meta-pile" data-pile="opp-hand" type="button" title="View opponent's hand">🃏 ${them.hand.length} hand</button>
              <button class="meta-pile" data-pile="opp-crew" type="button" title="View opponent's crew">👥 ${them.crew.length} crew</button>
              ${me.crew.some((c) => c.id === 'crows_nest') ? `<button class="meta-pile meta-pile--peek" id="btn-peek-reorder" type="button" title="Crow's Nest: peek + reorder their draw">🦅 Peek</button>` : ''}
              ${them.overboard?.length ? `<span class="meta-pill">🌊 ${them.overboard.length}</span>` : ''}
            </div>
          </div>
        `}
      </section>

      <!-- Action row: between ships, where the thumb naturally rests -->
      <div class="match-actions match-actions--inline">
        ${view.lastError ? `<div class="last-result">${escape(view.lastError)}</div>` : ''}
        <button id="btn-clear" class="btn btn--ghost" ${plank.length === 0 ? 'disabled' : ''}>Clear</button>
        <button id="btn-resolve" class="btn btn--primary" ${plank.length === 0 ? 'disabled' : ''}>⚓ Resolve turn</button>
      </div>

      <!-- Your captain panel — sits above the preview/plank so both ships are
           visible up top and the play area below is uncluttered. -->
      <div class="ship-zone ship-zone--me">
        <div class="captain captain--me">
          <div class="captain-name"><span class="captain-ship-icon" data-ship-side="me">${shipArt(me.shipId ?? m.shipId ?? 'brigantine')}</span>${escape(me.name)} <em>(you)</em></div>
          <div class="captain-loadout">${escape(captainLabel(me.captainId))} · ${escape(shipLabel(me.shipId ?? 'brigantine'))}</div>
          ${m.mode === 'pve' ? '' : `<div class="hull">🪙 <span class="hull-num" data-side="me">${view.shownDoubloons[me.id]}</span> <span class="hull-bar"><span style="width:${hullWidth(me.doubloons, m.winThreshold)}%"></span></span></div>`}
          <div class="captain-meta">
            <button class="meta-pile" data-pile="hand" type="button">🃏 ${me.hand.length}</button>
            <button class="meta-pile" data-pile="deck" type="button">🂠 ${me.draw.length + me.discard.length}</button>
            ${me.discard.length > 0 ? `<button class="meta-pile" data-pile="discard" type="button">♻ ${me.discard.length}</button>` : ''}
            <button class="meta-pile meta-pile--crew" data-pile="my-crew" type="button" title="View your crew">👥 ${me.crew.length} crew</button>
            <button class="meta-pile ${(me.overboard?.length ?? 0) > 0 ? 'meta-pile--danger' : 'meta-pile--muted'}" data-pile="overboard" type="button" title="Overboard pile — cards lost permanently this run">🌊 ${me.overboard?.length ?? 0}</button>
            ${me.pendingDiscards ? `<span class="meta-pending">🌊 ${me.pendingDiscards} next</span>` : ''}
          </div>
        </div>
      </div>

      <!-- Pattern preview above the plank: read top-to-bottom as
           "what's about to happen" → "the cards I'm queuing" → "my hand".
           Defaults to a compact summary; full stack is one click away. -->
      <div class="plank-preview">
        ${totalLine}
        ${stackLine}
        ${plank.length > 0 ? `<details class="preview-details">
          <summary>Stack details${detailCount > 0 ? ` (${detailCount})` : ''}</summary>
          <ul>${patternsPreview}</ul>
        </details>` : ''}
      </div>

      <!-- Plank: drop zone, directly above the hand for short drag distance -->
      <section class="plank-area">
        ${markNotice}
        <div class="plank-label">The Plank</div>
        <div class="plank">${plankHtml}</div>
      </section>

      <!-- Hand: bottom of screen for thumb reach + short drag to plank -->
      <section class="hand-area">
        <div class="hand-row">
          <div class="hand">${handHtml}</div>
          ${renderTrashBin(me)}
        </div>
      </section>
    </div>
  `;
}
function bindTurn(root, goToLobby) {
    const m = view.envelope.match;
    const me = m.players[view.envelope.perspective];
    const them = m.players[view.envelope.perspective === 'a' ? 'b' : 'a'];
    const bilgeN = me.crew.filter((c) => c.id === 'bilge_pump').length;
    const tossCap = Math.min(me.hand.length, 3 + bilgeN);
    void tossCap;
    // Drag-and-drop card system: tap toggles selection, drag positions onto
    // the plank, plank cards can be reordered or dragged off, and dragging
    // a hand card onto the trash bin tosses it for a fresh draw.
    attachDragHandlers(root, {
        onTap: (cardId, fromPlank) => {
            if (fromPlank) {
                view.selectedCardIds = view.selectedCardIds.filter((x) => x !== cardId);
            }
            else {
                if (view.selectedCardIds.includes(cardId)) {
                    view.selectedCardIds = view.selectedCardIds.filter((x) => x !== cardId);
                }
                else if (view.selectedCardIds.length < 5) {
                    view.selectedCardIds = [...view.selectedCardIds, cardId];
                }
            }
            render(root, goToLobby);
        },
        onDropToPlank: (cardId, targetIndex) => {
            // Always rebuild from a Set-deduped base so we never end up with
            // the same ID twice in selectedCardIds.
            const base = Array.from(new Set(view.selectedCardIds.filter((x) => x !== cardId)));
            if (!view.selectedCardIds.includes(cardId) && base.length >= 5) {
                // hand→plank but plank is full → no-op
                return;
            }
            const idx = Math.min(targetIndex, base.length);
            base.splice(idx, 0, cardId);
            view.selectedCardIds = base;
            render(root, goToLobby);
        },
        onReorderPlank: (cardId, targetIndex) => {
            const base = Array.from(new Set(view.selectedCardIds.filter((x) => x !== cardId)));
            const idx = Math.min(targetIndex, base.length);
            base.splice(idx, 0, cardId);
            view.selectedCardIds = base;
            render(root, goToLobby);
        },
        onRemoveFromPlank: (cardId) => {
            view.selectedCardIds = view.selectedCardIds.filter((x) => x !== cardId);
            render(root, goToLobby);
        },
        onDropToTrash: async (cardId, fromPlank) => {
            if (fromPlank) {
                // Don't allow tossing a plank card directly; remove from plank first
                view.selectedCardIds = view.selectedCardIds.filter((x) => x !== cardId);
                render(root, goToLobby);
                return;
            }
            // Single-card toss via API
            try {
                await api.tossCard(view.matchId, cardId);
                await refresh(root, goToLobby);
            }
            catch (err) {
                view.lastError = err instanceof ApiError ? err.message : err.message;
                render(root, goToLobby);
            }
        },
        onLongPress: (cardId, sourceEl) => {
            const card = me.hand.find((c) => c.id === cardId);
            if (!card)
                return;
            const big = sourceEl.cloneNode(true);
            big.classList.remove('pcard--selected', 'pcard--dragging');
            showCardPreview(`<div class="preview-card">${big.outerHTML}</div>`);
        },
    });
    root.querySelector('#btn-clear')?.addEventListener('click', () => {
        view.selectedCardIds = [];
        render(root, goToLobby);
    });
    root.querySelector('#btn-resolve')?.addEventListener('click', async () => {
        const ids = view.selectedCardIds;
        if (ids.length === 0)
            return;
        // Capture pre-resolve plank cards for pattern preview flourish
        const env = view.envelope;
        const me = env.match.players[env.perspective];
        const plank = ids.map((id) => me.hand.find((c) => c.id === id)).filter(Boolean);
        const previewPatterns = detectPatternsForPlayer(plank, me);
        // FLIP capture before mutation
        const before = captureCardRects(root);
        // Capture hull bar elements for coin-trail FX between captains
        const myHullEl = root.querySelector('.captain--me .hull');
        const themHullEl = root.querySelector('.captain--them .hull');
        const beforeMyDoubloons = me.doubloons;
        const beforeTheirDoubloons = env.match.players[env.perspective === 'a' ? 'b' : 'a'].doubloons;
        try {
            await api.submitTurn(view.matchId, ids);
            view.selectedCardIds = [];
            await refresh(root, goToLobby);
            playFlip(root, before, 600);
            playSfx('card_play');
            if (previewPatterns.length > 0) {
                // One condensed combo banner with all patterns + swing line, instead
                // of one banner per pattern cascading down the screen.
                let previewSwing;
                try {
                    previewSwing = resolveTurn(cloneState(env.match), plank).result.effects;
                }
                catch { /* preview-only; fall back to chips-only banner */ }
                flashComboBanner(previewPatterns.map((p) => ({ label: p.label, type: p.type })), previewSwing);
                // Pattern SFX + screen shakes still cascade so the audio still feels
                // like a building combo, but capped to the first 4 to avoid noise.
                previewPatterns.slice(0, 4).forEach((p, i) => {
                    setTimeout(() => {
                        playSfx(patternSfx(p.type));
                        if (p.type === 'mutiny')
                            screenShake('hard');
                        else if (p.type === 'straight-flush' || p.type === 'flush')
                            screenShake('medium');
                    }, 100 + i * 220);
                });
            }
            // Coin trail for stolen doubloons (opp hull → my hull)
            const newEnv = view.envelope;
            const newMe = newEnv.match.players[newEnv.perspective];
            const newThem = newEnv.match.players[newEnv.perspective === 'a' ? 'b' : 'a'];
            const myDelta = newMe.doubloons - beforeMyDoubloons;
            const theirDelta = newThem.doubloons - beforeTheirDoubloons;
            // If I gained AND they lost: that's a steal — animate trail.
            if (myDelta > 0 && theirDelta < 0 && themHullEl && myHullEl) {
                // Cannon volley: my ship → their ship. Volley size scales with damage.
                const myShip = root.querySelector('.captain--me .captain-ship-icon');
                const theirShip = root.querySelector('.captain--them .captain-ship-icon, .ante-panel .captain-ship-icon');
                if (myShip && theirShip) {
                    const volley = Math.min(5, Math.max(1, Math.round(-theirDelta / 2)));
                    myShip.classList.add('captain-ship-icon--firing');
                    setTimeout(() => myShip.classList.remove('captain-ship-icon--firing'), 500);
                    fireCannons(myShip, theirShip, volley, () => playSfx('cannon_impact'));
                    for (let i = 0; i < volley; i++) {
                        setTimeout(() => playSfx('cannon_fire'), 50 + i * 140);
                    }
                }
                setTimeout(() => {
                    coinTrail(themHullEl, myHullEl, Math.min(8, -theirDelta));
                    playSfx('coin_steal');
                    // Always pop a damage number on the opponent's bar so steals are visible.
                    damageNumber(-theirDelta, themHullEl, 'damage');
                    // Always pop the earn on yours too so the "I got it" half is felt.
                    damageNumber(myDelta, myHullEl, 'earn');
                    if (-theirDelta >= 6)
                        flashOverlay('gold', 200);
                }, 600);
            }
            // If I gained without their loss → from Treasury (general earn)
            if (myDelta > 0 && theirDelta >= 0 && myHullEl) {
                // PvE: even a Treasury earn is "plundering the galleon" — fire cannons.
                if (newEnv.match.mode === 'pve') {
                    const myShip = root.querySelector('.captain--me .captain-ship-icon');
                    const treasuryShip = root.querySelector('.ante-panel .captain-ship-icon');
                    if (myShip && treasuryShip) {
                        const volley = Math.min(5, Math.max(1, Math.round(myDelta / 3)));
                        myShip.classList.add('captain-ship-icon--firing');
                        setTimeout(() => myShip.classList.remove('captain-ship-icon--firing'), 500);
                        fireCannons(myShip, treasuryShip, volley, () => playSfx('cannon_impact'));
                        for (let i = 0; i < volley; i++) {
                            setTimeout(() => playSfx('cannon_fire'), 50 + i * 140);
                        }
                    }
                }
                setTimeout(() => {
                    floatingDelta(myHullEl, `+${myDelta} 🪙`, '#f0c75a');
                    playSfx(myDelta >= 8 ? 'coin_earn_large' : 'coin_earn_small');
                    damageNumber(myDelta, myHullEl, 'earn');
                    if (myDelta >= 8)
                        flashOverlay('gold', 200);
                }, newEnv.match.mode === 'pve' ? 600 : 200);
            }
            if (myDelta < 0 && myHullEl) {
                setTimeout(() => {
                    floatingDelta(myHullEl, `${myDelta} 🪙`, '#a83232');
                    playSfx('hull_hit');
                    screenShake(-myDelta >= 8 ? 'hard' : -myDelta >= 4 ? 'medium' : 'soft');
                    if (-myDelta >= 6)
                        flashOverlay('red', 200);
                    damageNumber(-myDelta, myHullEl, 'damage');
                }, 200);
            }
            // Opponent took damage from steal → hull_hit on their bar
            if (theirDelta < 0 && themHullEl) {
                setTimeout(() => {
                    playSfx('hull_hit');
                    screenShake(-theirDelta >= 8 ? 'medium' : 'soft');
                }, 600);
            }
            // Low-hull vignette — me only (so opponent's hull doesn't trigger overlays)
            if (myHullEl) {
                const newMy = newEnv.match.players[newEnv.perspective].doubloons;
                lowHullVignette(newMy < 8 && newEnv.match.status === 'in_progress');
            }
        }
        catch (err) {
            view.lastError = err instanceof ApiError ? err.message : err.message;
            render(root, goToLobby);
        }
    });
    root.querySelector('#btn-back')?.addEventListener('click', goToLobby);
    root.querySelector('#btn-log')?.addEventListener('click', () => showLogViewer(m));
    root.querySelector('#btn-peek-reorder')?.addEventListener('click', async () => {
        try {
            const peek = await api.peekOppDraw(view.matchId);
            if (peek.used) {
                alert("You've already used Crow's Nest this turn.");
                return;
            }
            showPeekBury(peek.cards, async (cardId) => {
                try {
                    await api.buryOppDraw(view.matchId, cardId);
                    await refresh(root, goToLobby);
                }
                catch (err) {
                    alert(err.message);
                }
            });
        }
        catch (err) {
            alert(err.message);
        }
    });
    // Card-name references in log → tooltip popover
    bindCardRefs(root);
    // Pile-viewer buttons in the captain panel
    root.querySelectorAll('button[data-pile]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const pile = btn.dataset.pile;
            if (pile === 'hand') {
                showPileViewer('Your Hand', `${me.hand.length} card${me.hand.length === 1 ? '' : 's'} ready to play`, me.hand);
            }
            else if (pile === 'deck') {
                const all = [...me.draw, ...me.discard];
                showPileViewer('Your Deck', `${all.length} cards remaining · ${me.draw.length} in draw pile · ${me.discard.length} in discard (cycles back when draw empties)`, all);
            }
            else if (pile === 'discard') {
                showPileViewer('Discard Pile', `${me.discard.length} cards · these reshuffle into your draw pile when it empties`, me.discard);
            }
            else if (pile === 'overboard') {
                showPileViewer('🌊 Overboard', `${(me.overboard ?? []).length} cards lost — gone for the rest of the match. (Salvage edict can recover some.)`, me.overboard ?? []);
            }
            else if (pile === 'my-crew') {
                showCrewViewer('Your Crew', me.crew, true, async (crewId) => {
                    try {
                        await api.sellCrew(view.matchId, crewId);
                        await refresh(root, goToLobby);
                    }
                    catch (err) {
                        view.lastError = err instanceof ApiError ? err.message : err.message;
                        render(root, goToLobby);
                    }
                });
            }
            else if (pile === 'opp-crew') {
                showCrewViewer(`${them.name}'s Crew`, them.crew, false);
            }
            else if (pile === 'opp-hand') {
                showPileViewer(`${them.name}'s Hand`, `${them.hand.length} card${them.hand.length === 1 ? '' : 's'} · async PvP — full visibility while you wait`, them.hand);
            }
        });
    });
    root.querySelectorAll('button[data-sell-crew]').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const crewId = btn.dataset.sellCrew;
            try {
                await api.sellCrew(view.matchId, crewId);
                await refresh(root, goToLobby);
            }
            catch (err) {
                view.lastError = err instanceof ApiError ? err.message : err.message;
                render(root, goToLobby);
            }
        });
    });
}
function renderRefreshControl(m, buyer) {
    const used = m.pveRefreshesUsed ?? 0;
    const cost = 2 + 2 * used;
    const affordable = buyer.doubloons - cost > 0;
    const nextCost = 2 + 2 * (used + 1);
    return `<div class="port-refresh-bar port-refresh-bar--prominent">
    <button id="btn-refresh" class="port-refresh-btn ${affordable ? '' : 'port-refresh-btn--no'}" ${affordable ? '' : 'disabled'}>
      <span class="port-refresh-icon">♻</span>
      <span class="port-refresh-stack">
        <span class="port-refresh-title">REFRESH OFFERS</span>
        <span class="port-refresh-cost">${cost}🪙 · next ${nextCost}🪙</span>
      </span>
    </button>
    <div class="port-refresh-meta">Used ${used}× this run. Cost climbs by +2 each time and never resets.</div>
  </div>`;
}
function renderAntePanel(m) {
    const ante = m.currentAnte ?? 1;
    const target = m.anteThresholds?.[ante - 1] ?? 0;
    const hands = m.anteHandsPlayed ?? 0;
    const limit = m.anteHandLimit ?? 4;
    const endless = m.endless;
    const me = m.players.a;
    const ratio = target > 0 ? Math.min(100, Math.max(0, (me.doubloons / target) * 100)) : 0;
    const onTrack = me.doubloons >= target;
    // Two-line compact layout:
    //   Line 1: galleon · "Ante 3 / 5"  ·  Hand 2/4
    //   Line 2: progress bar (🪙 14 / 65)
    // Target lives INSIDE the progress bar label so we don't need a separate
    // chip squeezing the title row.
    const ladder = !endless
        ? (m.anteThresholds ?? []).slice(0, 5).map((t, i) => `${i + 1 < ante ? '✓' : i + 1 === ante ? '►' : ''}🪙${t}`).join(' · ')
        : `Next: 🪙${target * 2}`;
    const titleText = endless
        ? `Ante ${ante} <span class="endless-tag">ENDLESS</span>`
        : `Ante ${ante} <span class="ante-of">of 5</span>`;
    return `<div class="ante-panel ante-panel--compact ${endless ? 'ante-panel--endless' : ''}">
    <div class="ante-row">
      <span class="captain-ship-icon captain-ship-icon--enemy ante-galleon" data-ship-side="them">${shipArt('galleon')}</span>
      <div class="ante-headline">
        <div class="ante-title-row" title="${escape(ladder)}">
          <span class="ante-title-tag">${titleText}</span>
          <span class="ante-hand-chip">Hand ${hands + 1} / ${limit}</span>
        </div>
        <div class="ante-progress-bar ante-progress-bar--compact">
          <div class="ante-progress-fill" style="width:${ratio}%"></div>
          <div class="ante-progress-label">🪙 ${me.doubloons} / ${target}${onTrack ? ' ✓' : ''}</div>
        </div>
      </div>
    </div>
  </div>`;
}
// Build once on module load — names don't change at runtime.
const CARD_REGISTRY = (() => {
    const reg = new Map();
    for (const c of getCardRegistry()) {
        reg.set(c.name.toLowerCase(), c);
    }
    // Pre-build a regex matching any registered name as a word.
    const escaped = [...reg.values()]
        .map((c) => c.name)
        .sort((a, b) => b.length - a.length) // longest first to avoid partial overlaps
        .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(`\\b(${escaped.join('|')})\\b`, 'g');
    return { lookup: reg, regex: re };
})();
function annotateLogText(text) {
    // Escape first, then run the regex on the escaped string. The card names
    // don't contain HTML-special chars, so the regex still matches.
    const safe = escape(text);
    return safe.replace(CARD_REGISTRY.regex, (match) => {
        const card = CARD_REGISTRY.lookup.get(match.toLowerCase());
        if (!card)
            return match;
        const dataName = match.replace(/"/g, '&quot;');
        return `<span class="card-ref card-ref--${card.kind}" data-card-name="${dataName}" tabindex="0">${match}</span>`;
    });
}
function showCardRefPopover(target, name) {
    closeCardRefPopover();
    const card = CARD_REGISTRY.lookup.get(name.toLowerCase());
    if (!card)
        return;
    const rect = target.getBoundingClientRect();
    const pop = document.createElement('div');
    pop.className = 'card-ref-popover';
    pop.id = 'card-ref-popover';
    pop.innerHTML = `
    <div class="card-ref-tag card-ref-tag--${card.kind}">${card.kind.toUpperCase()}</div>
    <div class="card-ref-name">${escape(card.name)}</div>
    <div class="card-ref-desc">${escape(card.description)}</div>
  `;
    document.body.appendChild(pop);
    // Position below the target if there's room, else above
    const popH = pop.offsetHeight;
    const popW = pop.offsetWidth;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow > popH + 8 ? rect.bottom + 6 : rect.top - popH - 6;
    let left = rect.left + rect.width / 2 - popW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popW - 8));
    pop.style.top = `${Math.max(8, top)}px`;
    pop.style.left = `${left}px`;
    void pop.offsetWidth;
    pop.classList.add('card-ref-popover--in');
    const close = (e) => {
        if (e && (e.target === target || target.contains(e.target)))
            return;
        closeCardRefPopover();
        document.removeEventListener('pointerdown', close);
        document.removeEventListener('keydown', escListener);
    };
    const escListener = (e) => {
        if (e.key === 'Escape') {
            closeCardRefPopover();
            document.removeEventListener('keydown', escListener);
            document.removeEventListener('pointerdown', close);
        }
    };
    setTimeout(() => {
        document.addEventListener('pointerdown', close);
        document.addEventListener('keydown', escListener);
    }, 50);
}
function closeCardRefPopover() {
    document.getElementById('card-ref-popover')?.remove();
}
function bindCardRefs(root) {
    root.querySelectorAll('.card-ref').forEach((el) => {
        el.addEventListener('click', (e) => {
            e.stopPropagation();
            const name = el.dataset.cardName;
            showCardRefPopover(el, name);
        });
    });
}
function showPileViewer(title, subtitle, cards) {
    // Group by suit for easy scanning, then sort each group by rank.
    const RANK_ORDER = { A: 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 11, Q: 12, K: 13, '*': 14 };
    const SUIT_ORDER = { spades: 0, hearts: 1, diamonds: 2, clubs: 3, '*': 4 };
    const sorted = [...cards].sort((a, b) => {
        const so = (SUIT_ORDER[a.suit] ?? 99) - (SUIT_ORDER[b.suit] ?? 99);
        if (so !== 0)
            return so;
        return (RANK_ORDER[a.rank] ?? 99) - (RANK_ORDER[b.rank] ?? 99);
    });
    const overlay = document.createElement('div');
    overlay.className = 'pile-overlay';
    overlay.innerHTML = `
    <div class="pile-modal" role="dialog" aria-label="${title}">
      <div class="pile-header">
        <div>
          <h3>${title}</h3>
          <div class="pile-subtitle">${subtitle}</div>
        </div>
        <button class="pile-close" aria-label="Close">✕</button>
      </div>
      <div class="pile-grid">
        ${sorted.length === 0
        ? '<div class="pile-empty">— empty —</div>'
        : sorted.map((c) => renderPlayingCard(c, { tiny: true })).join('')}
      </div>
    </div>
  `;
    document.body.appendChild(overlay);
    void overlay.offsetWidth;
    overlay.classList.add('pile-overlay--in');
    const close = () => overlay.remove();
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay)
            close();
    });
    overlay.querySelector('.pile-close')?.addEventListener('click', close);
    const esc = (e) => {
        if (e.key === 'Escape') {
            close();
            document.removeEventListener('keydown', esc);
        }
    };
    document.addEventListener('keydown', esc);
}
function showPeekBury(cards, onPick) {
    const overlay = document.createElement('div');
    overlay.className = 'pile-overlay';
    overlay.innerHTML = `
    <div class="pile-modal pile-modal--peek" role="dialog" aria-label="Bury a card">
      <div class="pile-header">
        <div>
          <h3>🦅 Crow's Nest — Send one to the depths</h3>
          <div class="pile-subtitle">These are the top ${cards.length} of their draw pile. Tap one to bury it at the BOTTOM. They'll have to cycle through everything else before it comes back.</div>
        </div>
        <button class="pile-close" aria-label="Close">✕</button>
      </div>
      <div class="peek-row">
        ${cards.map((c, i) => `
          <div class="peek-slot">
            <div class="peek-position">${i + 1}</div>
            <button class="peek-card peek-card--btn" data-card-id="${c.id}" type="button">${renderPlayingCard(c, { tiny: true })}</button>
          </div>`).join('')}
      </div>
      <div class="peek-actions">
        <button class="btn btn--ghost peek-cancel" type="button">Cancel</button>
      </div>
    </div>`;
    document.body.appendChild(overlay);
    void overlay.offsetWidth;
    overlay.classList.add('pile-overlay--in');
    const close = () => overlay.remove();
    overlay.addEventListener('click', (e) => { if (e.target === overlay)
        close(); });
    overlay.querySelector('.pile-close')?.addEventListener('click', close);
    overlay.querySelector('.peek-cancel')?.addEventListener('click', close);
    overlay.querySelectorAll('.peek-card--btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const cardId = btn.dataset.cardId;
            onPick(cardId);
            close();
        });
    });
    const esc = (e) => {
        if (e.key === 'Escape') {
            close();
            document.removeEventListener('keydown', esc);
        }
    };
    document.addEventListener('keydown', esc);
}
function showLogViewer(m) {
    const overlay = document.createElement('div');
    overlay.className = 'pile-overlay';
    const entries = [...m.log].reverse();
    overlay.innerHTML = `
    <div class="pile-modal pile-modal--log" role="dialog" aria-label="Match log">
      <div class="pile-header">
        <div>
          <h3>📜 Match Log</h3>
          <div class="pile-subtitle">${entries.length} entr${entries.length === 1 ? 'y' : 'ies'} · newest first · tap a card name for details</div>
        </div>
        <button class="pile-close" aria-label="Close">✕</button>
      </div>
      <div class="log-list">
        ${entries.length === 0 ? '<div class="pile-empty">— no log entries yet —</div>'
        : entries.map((l) => `<div class="log-entry">${annotateLogText(l.text)}</div>`).join('')}
      </div>
    </div>`;
    document.body.appendChild(overlay);
    void overlay.offsetWidth;
    overlay.classList.add('pile-overlay--in');
    const close = () => overlay.remove();
    overlay.addEventListener('click', (e) => { if (e.target === overlay)
        close(); });
    overlay.querySelector('.pile-close')?.addEventListener('click', close);
    bindCardRefs(overlay);
    const esc = (e) => {
        if (e.key === 'Escape') {
            close();
            document.removeEventListener('keydown', esc);
        }
    };
    document.addEventListener('keydown', esc);
}
function showCrewViewer(title, crew, sellable, onSell) {
    const overlay = document.createElement('div');
    overlay.className = 'pile-overlay';
    const cards = crew.length === 0
        ? '<div class="pile-empty">— no crew yet — buy at the Port —</div>'
        : crew.map((c) => renderCrewCard(c, { compact: false, sellable })).join('');
    overlay.innerHTML = `
    <div class="pile-modal pile-modal--crew" role="dialog" aria-label="${title}">
      <div class="pile-header">
        <div>
          <h3>${title}</h3>
          <div class="pile-subtitle">${crew.length} crew · their effects fire automatically each turn</div>
        </div>
        <button class="pile-close" aria-label="Close">✕</button>
      </div>
      <div class="crew-grid">${cards}</div>
    </div>
  `;
    document.body.appendChild(overlay);
    void overlay.offsetWidth;
    overlay.classList.add('pile-overlay--in');
    const close = () => overlay.remove();
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay)
            close();
    });
    overlay.querySelector('.pile-close')?.addEventListener('click', close);
    if (sellable && onSell) {
        overlay.querySelectorAll('button[data-sell-crew]').forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                onSell(btn.dataset.sellCrew);
                close();
            });
        });
    }
    const esc = (e) => {
        if (e.key === 'Escape') {
            close();
            document.removeEventListener('keydown', esc);
        }
    };
    document.addEventListener('keydown', esc);
}
function renderTrashBin(me) {
    const used = me.tossesUsedThisTurn ?? 0;
    const bilgeN = me.crew.filter((c) => c.id === 'bilge_pump').length;
    const coxN = me.crew.filter((c) => c.id === 'coxswain').length;
    const cap = 4 + bilgeN + 2 * coxN;
    const left = Math.max(0, cap - used);
    const exhausted = left <= 0;
    return `<div class="trash-bin ${exhausted ? 'trash-bin--exhausted' : ''}" data-trash-zone="1">
    <div class="trash-icon">${'\u{1F5D1}'}</div>
    <div class="trash-stack">
      <div class="trash-title">TOSS</div>
      <div class="trash-count">${left} <span class="muted">/ ${cap} left</span></div>
    </div>
  </div>`;
}
function renderWaitingForJoin(m, _persp) {
    return `
    <div class="waiting-screen">
      <h2>Waiting for opponent…</h2>
      <p>Share the invite code from the lobby.</p>
      <div class="match-id muted">match ${escape(m.id)}</div>
      <button id="back" class="btn btn--primary">Back to lobby</button>
    </div>
  `;
}
function renderWaiting(m, them, what, subtitle) {
    const last = view.lastPollAt ? new Date(view.lastPollAt).toLocaleTimeString() : 'never';
    const me = m.players[view.envelope.perspective];
    const myHull = me.doubloons;
    // Spectator: hand stays hidden (only the count is shown). What's revealed
    // instead is their STAGED PLANK — cards they've dragged onto the play
    // area but not yet submitted. Polling refreshes this every 1.5s, so the
    // waiting player watches the plank fill up live as the opponent plans.
    const staged = them.stagedPlank ?? [];
    const stagedHtml = staged.length > 0
        ? staged.map((c) => renderPlayingCard(c)).join('')
        : '<div class="spectator-plank-empty muted">— plank empty · they\'re still picking —</div>';
    const crewHtml = them.crew.length > 0
        ? them.crew.slice(0, 8).map((c) => `<span class="crew-mini" title="${escape(c.description ?? '')}">${escape(c.name ?? '?')}</span>`).join('')
        : '<span class="muted">no crew</span>';
    return `
    <div class="match-view match-view--v2 spectator-view">
      <div class="match-topbar">
        <button id="manual-refresh" class="match-icon-btn" type="button" title="Refresh now">↻</button>
        <div class="turn-indicator">
          <span class="mode-badge mode-badge--pvp">⚔ ${escape(them.name)} ${escape(what)}</span>
        </div>
        <button id="back" class="btn btn--ghost btn--small" type="button">Back to lobby</button>
      </div>

      <section class="ship-zone ship-zone--them spectator-them">
        <div class="captain captain--them">
          <div class="captain-name"><span class="captain-ship-icon">${shipArt(them.shipId ?? m.shipId ?? 'brigantine')}</span>${escape(them.name)} <span class="thinking-pulse">⋯ thinking</span></div>
          <div class="captain-loadout">${escape(captainLabel(them.captainId))} · ${escape(shipLabel(them.shipId ?? 'brigantine'))}</div>
          <div class="hull">🪙 ${them.doubloons} <span class="hull-bar"><span style="width:${hullWidth(them.doubloons, m.winThreshold)}%"></span></span></div>
        </div>
        <div class="spectator-plank">
          <div class="spectator-label">Their plank <span class="muted">${staged.length}/5 staged · ${them.hand.length} in hand</span></div>
          <div class="plank">${stagedHtml}</div>
        </div>
        <div class="spectator-crew">
          <div class="spectator-label">Their crew</div>
          <div class="crew-mini-row">${crewHtml}</div>
        </div>
      </section>

      <div class="ship-zone ship-zone--me spectator-me">
        <div class="captain captain--me">
          <div class="captain-name"><span class="captain-ship-icon">${shipArt(me.shipId ?? 'brigantine')}</span>${escape(me.name)} <em>(you)</em></div>
          <div class="captain-loadout">${escape(captainLabel(me.captainId))} · ${escape(shipLabel(me.shipId ?? 'brigantine'))}</div>
          <div class="hull">🪙 ${myHull} <span class="hull-bar"><span style="width:${hullWidth(myHull, m.winThreshold)}%"></span></span></div>
        </div>
      </div>

      <div class="spectator-foot">
        <button id="btn-log" class="btn btn--ghost" type="button">📜 Match log</button>
        <span class="poll-meta">${escape(subtitle ?? 'Polling every 1.5s')} · last ${escape(last)}</span>
      </div>
    </div>
  `;
}
function renderShop(m, buyer) {
    if (!m.shop)
        return '<div>No port to visit.</div>';
    const isPve = m.mode === 'pve';
    const opp = m.players[buyer.id === 'a' ? 'b' : 'a'];
    const total = m.shop.pickOrder.length;
    const picksDone = m.shop.currentPick;
    const offerCard = (o) => {
        const purchased = !!o.purchased;
        const affordable = !purchased && buyer.doubloons - o.cost > 0;
        const kindLabel = o.kind === 'crew' ? 'CREW' : o.kind === 'edict' ? 'EDICT' : 'DECK MOD';
        const offerClass = purchased ? 'port-offer port-offer--purchased' : `port-offer ${affordable ? 'port-offer--ok' : 'port-offer--no'}`;
        const buyButton = purchased
            ? `<div class="port-buy port-buy--owned"><span class="port-buy-label">✓ Bought</span><span class="port-buy-cost">${o.cost}🪙</span></div>`
            : `<button class="port-buy ${affordable ? '' : 'port-buy--disabled'}" data-offer="${escape(o.id)}" ${affordable ? '' : 'disabled'}>
          <span class="port-buy-label">${affordable ? 'BUY' : "CAN'T AFFORD"}</span>
          <span class="port-buy-cost">${o.cost}🪙</span>
        </button>`;
        if (o.kind === 'crew') {
            const data = o.data;
            return `<div class="${offerClass}" data-offer-id="${escape(o.id)}">
        <div class="port-offer-tag">${kindLabel}</div>
        <div class="port-offer-content">${renderCrewCard(data, {})}</div>
        ${buyButton}
      </div>`;
        }
        const generic = o.data;
        const kindClass = o.kind === 'edict' ? 'port-card--edict' : 'port-card--mod';
        return `<div class="${offerClass}" data-offer-id="${escape(o.id)}">
      <div class="port-offer-tag">${kindLabel}</div>
      <div class="port-offer-content">
        <div class="port-card ${kindClass}">
          <div class="port-card-icon">${o.kind === 'edict' ? '⚔' : '⚒'}</div>
          <div class="port-card-name">${escape(generic.name)}</div>
          <div class="port-card-desc">${escape(generic.description)}</div>
        </div>
      </div>
      ${buyButton}
    </div>`;
    };
    const offersHtml = m.shop.offers.length > 0
        ? m.shop.offers.map(offerCard).join('')
        : '<div class="port-empty">All offers claimed.</div>';
    const sellHtml = buyer.crew.length > 0
        ? buyer.crew.map((c) => renderCrewCard(c, { compact: true, sellable: true })).join('')
        : '<div class="port-empty-mini">No crew aboard yet.</div>';
    return `
    <div class="port-view">
      <header class="port-header">
        <div class="port-header-title">
          <div class="port-icon">⚓</div>
          <div>
            <h2>The Port</h2>
            <div class="port-subtitle">${isPve ? `Ante ${m.currentAnte} cleared. Spend, then push on.` : "Less-money captain picks first."}</div>
          </div>
        </div>
        <div class="port-header-stats">
          <div class="port-pick-info">${isPve ? 'Solo run' : `Pick ${picksDone + 1}/${total}`}</div>
          <div class="port-doubloons">
            <div class="port-captain-row port-captain-row--me">
              <span class="port-captain-name">${escape(buyer.name)}</span>
              <span class="port-captain-coin">🪙 ${buyer.doubloons}</span>
            </div>
            ${isPve ? '' : `<div class="port-captain-row">
              <span class="port-captain-name">${escape(opp.name)}</span>
              <span class="port-captain-coin">🪙 ${opp.doubloons}</span>
            </div>`}
          </div>
          <div class="port-piles">
            <button class="meta-pile" data-pile="deck" type="button">🂠 ${buyer.draw.length + buyer.discard.length} deck</button>
            ${buyer.discard.length > 0 ? `<button class="meta-pile" data-pile="discard" type="button">♻ ${buyer.discard.length} discard</button>` : ''}
            ${buyer.overboard?.length ? `<button class="meta-pile meta-pile--danger" data-pile="overboard" type="button">🌊 ${buyer.overboard.length} overboard</button>` : '<span class="meta-pile meta-pile--none">🌊 0 overboard</span>'}
          </div>
        </div>
      </header>

      ${isPve ? renderRefreshControl(m, buyer) : ''}

      <div class="port-quick-actions">
        <button class="btn btn--primary btn--big port-pass-btn" data-action="pass">${isPve ? '⚓ Set sail — next ante' : (m.shop.offers.length > 0 ? 'Pass — set sail' : 'Set sail')}</button>
        <button id="btn-back" class="btn btn--ghost">Back to lobby</button>
      </div>

      <div class="port-body">
        <section class="port-offers" data-port-pane="offers">
          <div class="port-section-title">Offers <span class="muted">— click to buy · hold to enlarge${isPve ? ' · buy as many as you can afford' : ''}</span></div>
          <div class="port-offers-grid">${offersHtml}</div>
        </section>

        <section class="port-sell" data-port-pane="sell">
          <details class="port-sell-details">
            <summary class="port-sell-summary">
              <span class="port-sell-title">👥 Your Crew (${buyer.crew.length})</span>
              <span class="port-sell-hint muted">tap to expand · sell for 2/3 cost</span>
            </summary>
            <div class="port-sell-grid">${sellHtml}</div>
          </details>
        </section>
      </div>

      ${view.lastError ? `<div class="last-result">${escape(view.lastError)}</div>` : ''}
    </div>
  `;
}
function bindShop(root, goToLobby) {
    const env = view.envelope;
    const m = env.match;
    const buyer = m.players[env.perspective];
    // Pile-viewer buttons in the port header
    root.querySelectorAll('button[data-pile]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const pile = btn.dataset.pile;
            if (pile === 'deck') {
                const all = [...buyer.draw, ...buyer.discard];
                showPileViewer('Your Deck', `${all.length} cards remaining · ${buyer.draw.length} in draw · ${buyer.discard.length} in discard`, all);
            }
            else if (pile === 'discard') {
                showPileViewer('Discard Pile', `${buyer.discard.length} cards · cycles back into draw when it empties`, buyer.discard);
            }
            else if (pile === 'overboard') {
                showPileViewer('🌊 Overboard', `${(buyer.overboard ?? []).length} cards lost — Salvage edict can recover 2 of these.`, buyer.overboard ?? []);
            }
        });
    });
    // Card-name references in any visible log/text
    bindCardRefs(root);
    // Tabs (mobile)
    root.querySelectorAll('.port-tab').forEach((btn) => {
        btn.addEventListener('click', () => {
            const tab = btn.dataset.portTab;
            root.querySelectorAll('.port-tab').forEach((t) => t.classList.toggle('port-tab--active', t.dataset.portTab === tab));
            root.querySelectorAll('[data-port-pane]').forEach((pane) => {
                pane.classList.toggle('port-pane--hidden-mobile', pane.dataset.portPane !== tab);
            });
        });
    });
    root.querySelectorAll('button[data-offer]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const meRow = root.querySelector('.port-captain-row--me');
            const offerEl = btn.closest('.port-offer');
            const cost = btn.querySelector('.port-buy-cost')?.textContent ?? '';
            try {
                await api.shopAction(view.matchId, 'buy', btn.dataset.offer);
                if (meRow && offerEl)
                    coinTrail(meRow, offerEl, 5);
                playSfx('shop_buy');
                // In-place patch instead of full match-view re-render. The full
                // re-render was clobbering innerHTML which painted a blank frame
                // (the visible flash). We update only what the buy actually
                // changed: this offer's slot + the player's doubloon counter +
                // crew/hand counters in the captain meta row.
                if (offerEl) {
                    offerEl.classList.remove('port-offer--ok', 'port-offer--no');
                    offerEl.classList.add('port-offer--purchased');
                    btn.outerHTML = `<div class="port-buy port-buy--owned"><span class="port-buy-label">✓ Bought</span><span class="port-buy-cost">${cost}</span></div>`;
                }
                // Sync server state silently (no DOM clobber). View is updated for
                // the next interaction; the visible numbers below get patched too.
                try {
                    const env = await api.getMatch(view.matchId);
                    view.envelope = env;
                    const me = env.match.players[env.perspective];
                    // Disable any other buy buttons that are now unaffordable.
                    root.querySelectorAll('button.port-buy[data-offer]').forEach((b) => {
                        const c = parseInt(b.querySelector('.port-buy-cost')?.textContent?.replace(/\D/g, '') ?? '0', 10);
                        if (me.doubloons - c <= 0) {
                            b.setAttribute('disabled', 'true');
                            b.classList.add('port-buy--disabled');
                            const label = b.querySelector('.port-buy-label');
                            if (label)
                                label.textContent = "CAN'T AFFORD";
                        }
                    });
                    // Update player doubloon counter (header strip if shown)
                    root.querySelectorAll('.port-captain-row--me .port-doubloons, .captain--me .hull-num').forEach((el) => {
                        el.textContent = String(me.doubloons);
                    });
                }
                catch { /* best-effort sync; non-fatal */ }
            }
            catch (err) {
                view.lastError = err instanceof ApiError ? err.message : err.message;
                render(root, goToLobby);
            }
        });
    });
    // Long-press on offer or crew → enlarged preview
    root.querySelectorAll('.port-offer-content, .port-sell-grid .crew').forEach((el) => {
        attachLongPressPreview(el, () => {
            const clone = el.cloneNode(true);
            clone.querySelectorAll('button, .crew-sell, .port-buy').forEach((b) => b.remove());
            return `<div class="preview-card preview-card--big">${clone.outerHTML}</div>`;
        });
    });
    root.querySelectorAll('.port-pass-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
            try {
                await api.shopAction(view.matchId, 'pass');
                await refresh(root, goToLobby);
            }
            catch (err) {
                view.lastError = err instanceof ApiError ? err.message : err.message;
                render(root, goToLobby);
            }
        });
    });
    root.querySelector('#btn-back')?.addEventListener('click', goToLobby);
    root.querySelector('#btn-refresh')?.addEventListener('click', async () => {
        const meRow = root.querySelector('.port-captain-row--me');
        const refreshBtn = root.querySelector('#btn-refresh');
        try {
            await api.refreshPort(view.matchId);
            if (meRow && refreshBtn)
                coinTrail(meRow, refreshBtn, 4);
            playSfx('shop_refresh');
            // Brief flash on the offers grid
            root.querySelector('.port-offers-grid')?.classList.add('port-offers-grid--reroll');
            setTimeout(async () => { await refresh(root, goToLobby); }, 500);
        }
        catch (err) {
            view.lastError = err instanceof ApiError ? err.message : err.message;
            render(root, goToLobby);
        }
    });
    root.querySelectorAll('button[data-sell-crew]').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const meRow = root.querySelector('.port-captain-row--me');
            const crewEl = btn.closest('.crew');
            const crewId = btn.dataset.sellCrew;
            try {
                await api.sellCrew(view.matchId, crewId);
                if (meRow && crewEl)
                    coinTrail(crewEl, meRow, 4);
                crewEl?.classList.add('crew--sold');
                playSfx('coin_sell');
                setTimeout(async () => { await refresh(root, goToLobby); }, 600);
            }
            catch (err) {
                view.lastError = err instanceof ApiError ? err.message : err.message;
                render(root, goToLobby);
            }
        });
    });
}
function renderEndScreen(m, perspective) {
    const winner = m.winner ? m.players[m.winner] : null;
    const youWon = m.winner === perspective;
    const isPve = m.mode === 'pve';
    const reasonText = m.endReason === 'sink'
        ? (isPve ? 'Your ship sank.' : `${escape(winner?.name ?? 'Opponent')} sank the rival.`)
        : m.endReason === 'plunder'
            ? (isPve ? `Plundered through ${m.anteThresholds?.length ?? 5} antes.` : `${escape(winner?.name ?? '')} plundered enough.`)
            : m.endReason === 'failed-ante'
                ? `Failed Ante ${m.currentAnte ?? '?'} (target 🪙${m.anteThresholds?.[(m.currentAnte ?? 1) - 1] ?? '?'}).`
                : '—';
    return `
    <div class="end-screen">
      <div class="end-screen-stats">
        <div>${escape(m.players.a.name)}: 🪙 ${m.players.a.doubloons}</div>
        ${isPve ? '' : `<div>${escape(m.players.b.name)}: 🪙 ${m.players.b.doubloons}</div>`}
      </div>
      <div class="end-screen-actions">
        <button id="rematch" class="btn btn--primary btn--big">⚓ ${isPve ? 'New solo run' : 'Rematch'}</button>
        <button id="back" class="btn btn--ghost">Back to lobby</button>
      </div>
    </div>
    <div id="end-stage" data-won="${youWon}" data-reason="${m.endReason ?? ''}" data-pve="${isPve}" data-name="${escape(winner?.name ?? m.players.a.name)}"
         data-reason-text="${reasonText}"></div>
  `;
}
function bindBack(root, goToLobby) {
    root.querySelector('#back, #btn-back')?.addEventListener('click', goToLobby);
    root.querySelector('#manual-refresh')?.addEventListener('click', () => {
        refresh(root, goToLobby);
    });
    // Spectator log button
    const m = view?.envelope?.match;
    if (m) {
        root.querySelector('#btn-log')?.addEventListener('click', () => showLogViewer(m));
    }
    bindCardRefs(root);
    // End-screen ceremony hookup
    const stage = root.querySelector('#end-stage');
    if (stage && !stage.dataset.staged) {
        stage.dataset.staged = '1';
        const won = stage.dataset.won === 'true';
        const name = stage.dataset.name ?? '';
        const reason = stage.dataset.reasonText ?? '';
        const overlay = won ? showWinCeremony(name, reason) : showLoseCeremony(reason || 'Defeat', '');
        overlay.addEventListener('click', () => overlay.remove());
        playSfx(won ? 'win_fanfare' : 'lose_fanfare');
        if (!won) {
            setTimeout(() => playSfx('ship_sink'), 1200);
            setTimeout(() => screenShake('hard'), 1300);
        }
        else {
            setTimeout(() => flashOverlay('gold', 380), 200);
        }
        lowHullVignette(false);
    }
    root.querySelector('#rematch')?.addEventListener('click', async () => {
        document.querySelectorAll('.end-ceremony').forEach((n) => n.remove());
        try {
            const env = view?.envelope;
            if (env?.match.mode === 'pve') {
                const created = await api.createPveMatch();
                location.hash = `#/match/${created.match_id}`;
            }
            else {
                const created = await api.createMatch();
                prompt(`Share this code:\n${created.invite_code}`, created.invite_code);
                location.hash = `#/match/${created.match_id}`;
            }
        }
        catch (err) {
            alert(err.message);
        }
    });
}
function hullWidth(doubloons, threshold) {
    return Math.max(0, Math.min(100, (doubloons / threshold) * 100));
}
function escape(s) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return s.replace(/[&<>"']/g, (ch) => map[ch]);
}
